import { Table, Tag } from "@caprikorn/dynamo-ui";
import type { FetchDataProps } from "@caprikorn/dynamo-ui/lib/components/dataDisplay/Table/Table.types";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { useSelectedPerimeters } from "@contexts/SelectedPerimeters/SelectedPerimeters.context";
import { FileCsv, FilePdf, LayerTriple } from "@icons";
import type { UrlParam } from "@router/router.types";
import type { Contract } from "@services/apiEcms/apiEcms.types";
import { useGetContracts } from "@services/apiEcms/useGetContracts";
import { usePostDocumentsDownload } from "@services/apiEcms/usePostDocumentsDownload";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { usePerimeterConctractsTableColumns } from "./PerimeterContractsTable.columns";
import classes from "./PerimeterContractsTable.module.css";

const initialFilters: FetchDataProps = {
  pageIndex: 0,
  pageSize: 10,
  globalFilter: "",
  columnFilters: [],
  sorting: [],
};

const getRowId = (row: Contract) => row.contractInformation.contractId;

export const PerimeterContractsTable = () => {
  const { t } = useCustomTranslation("main");
  const { customerId } = useParams<UrlParam>();
  const { setSelectedPerimeters } = useSelectedPerimeters();

  const [filters, setFilters] = useState<FetchDataProps>(initialFilters);

  const { isLoading, data, isError } = useGetContracts({
    pageNumber: filters.pageIndex + 1,
    elementsPerPage: filters.pageSize,
    customerId,
    search: filters.globalFilter,
  });
  const {
    mutate: downloadBill,
    variables: downloadBillVariables,
    isPending: isDownloadBillPending,
  } = usePostDocumentsDownload();

  const columns = usePerimeterConctractsTableColumns();

  return (
    <div className={classes.container}>
      {Boolean(data?.paging?.totalElements) && (
        <Tag
          icon={LayerTriple}
          iconStyle="start"
          label={t("perimeter.contracts.countLabel", {
            count: data?.paging.totalElements,
          })}
          variant="primary"
        />
      )}

      <Table
        columns={columns}
        data={data?.contracts || []}
        data-testid="PerimeterContractsTableProvider-table"
        fetchData={setFilters}
        getRowId={getRowId}
        hasRowSelection
        isError={isError}
        isLoading={isLoading}
        onSelectionChange={(rows) => {
          setSelectedPerimeters(
            rows.map((row) => row.original.contractInformation),
          );
        }}
        rowActions={(row) =>
          (["pdf", "csv"] as const).map((extension) => ({
            label: t("perimeter.contracts.rowActions.download", {
              format: extension.toLocaleUpperCase(),
            }),
            icon: extension === "csv" ? FileCsv : FilePdf,
            isLoading:
              isDownloadBillPending &&
              row.contractInformation.contractId ===
                downloadBillVariables?.contractId &&
              downloadBillVariables?.extension === extension,
            onClick: () => {
              downloadBill({
                customerId: row.contractInformation.customerId,
                contractId: row.contractInformation.contractId,
                extension,
                family: "invoice",
              });
            },
            tooltip: t("perimeter.contracts.rowActions.download", {
              format: extension.toLocaleUpperCase(),
            }),
          }))
        }
        totalCount={data?.paging.totalElements || 0}
      >
        <Table.ActionBar>
          <Table.ActionBar.FilterBar
            hasGlobalFilter
            searchPlaceholder={t("perimeter.contracts.searchPlaceholder")}
          />
        </Table.ActionBar>
        <Table.Content />
        <Table.Pagination withPageSizeSelector />
      </Table>
    </div>
  );
};
